import { zodResolver } from '@hookform/resolvers/zod'
import { useEffect } from 'react'
import { useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { toast } from 'sonner'
import { z } from 'zod'

import { Button } from '@/components/ui/button'
import { Checkbox } from '@/components/ui/checkbox'
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import {
  Form,
  FormControl,
  FormDescription,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from '@/components/ui/form'
import { Label } from '@/components/ui/label'
import { Switch } from '@/components/ui/switch'
import { EshopPricesFields } from '@/forms/components/EshopPricesFields'
import {
  allowedPaymentMethodsForDelivery,
  DeliveryMethod,
  paymentMethodTypes,
  productTypes,
} from '@/interfaces/eshop'
import API from '@/lib/api'
import { extractErrorMessage } from '@/lib/api/errorUtils'
import { buildPriceRows, buildPricesPayload } from '@/lib/eshop/prices'

const schema = z.object({
  isEnabled: z.boolean(),
  supportedProductTypes: z.array(z.enum(productTypes)).min(1),
  allowedPaymentMethods: z.array(z.enum(paymentMethodTypes)).min(1),
  prices: z.array(
    z.object({
      currency: z.string(),
      amount: z.number().min(0),
      freeFrom: z.number().min(0).nullable(),
    }),
  ),
})

type DeliveryMethodFormValues = z.infer<typeof schema>

interface DeliveryMethodFormProps {
  open: boolean
  onOpenChange: (open: boolean) => void
  method: DeliveryMethod | null
  currencies: string[]
  onSuccess: () => void
}

export function DeliveryMethodForm({
  open,
  onOpenChange,
  method,
  currencies,
  onSuccess,
}: DeliveryMethodFormProps) {
  const { t } = useTranslation(undefined, {
    keyPrefix: 'eshop.deliveryMethods',
  })
  const { t: te } = useTranslation(undefined, {
    keyPrefix: 'eshop',
  })

  const form = useForm<DeliveryMethodFormValues>({
    resolver: zodResolver(schema),
    defaultValues: {
      isEnabled: false,
      supportedProductTypes: [],
      allowedPaymentMethods: [],
      prices: [],
    },
  })

  useEffect(() => {
    if (!open || !method) return
    form.reset({
      isEnabled: method.isEnabled,
      supportedProductTypes: method.supportedProductTypes,
      allowedPaymentMethods: method.allowedPaymentMethods,
      prices: buildPriceRows(currencies, method.prices),
    })
  }, [open, method, currencies, form])

  if (!method) return null

  const availablePaymentMethods = allowedPaymentMethodsForDelivery(method.type)

  const onSubmit = async (values: DeliveryMethodFormValues) => {
    try {
      // Empty arrays mean "no change" on the backend; zod min(1) guarantees
      // we never send them, so the update is always a full replacement.
      await API.patch(`/api/v2/delivery-methods/${method.type}`, {
        isEnabled: values.isEnabled,
        supportedProductTypes: values.supportedProductTypes,
        allowedPaymentMethods: values.allowedPaymentMethods,
        prices: buildPricesPayload(values.prices),
      })
      toast.success(t('edit.success'))
      onOpenChange(false)
      onSuccess()
    } catch (error: unknown) {
      toast.error(t('edit.error'), {
        description: extractErrorMessage(error),
      })
    }
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[90vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{t(`types.${method.type}`)}</DialogTitle>
        </DialogHeader>
        <Form {...form}>
          <form
            onSubmit={form.handleSubmit(onSubmit)}
            className="flex flex-col gap-6"
          >
            <FormField
              control={form.control}
              name="isEnabled"
              render={({ field }) => (
                <FormItem className="flex flex-row items-center justify-between rounded-lg border p-3">
                  <FormLabel>{t('fields.isEnabled')}</FormLabel>
                  <FormControl>
                    <Switch
                      checked={field.value}
                      onCheckedChange={field.onChange}
                    />
                  </FormControl>
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="supportedProductTypes"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.supportedProductTypes')}</FormLabel>
                  <div className="flex flex-col gap-2">
                    {productTypes.map((type) => (
                      <FormItem
                        key={type}
                        className="flex flex-row items-center space-x-2 space-y-0"
                      >
                        <FormControl>
                          <Checkbox
                            checked={field.value.includes(type)}
                            onCheckedChange={(checked) => {
                              field.onChange(
                                checked === true
                                  ? [...field.value, type]
                                  : field.value.filter((v) => v !== type),
                              )
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {te(`productTypes.${type}`)}
                        </FormLabel>
                      </FormItem>
                    ))}
                  </div>
                  {field.value.length === 0 && (
                    <FormDescription className="text-destructive">
                      {t('fields.supportedProductTypesRequired')}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <FormField
              control={form.control}
              name="allowedPaymentMethods"
              render={({ field }) => (
                <FormItem>
                  <FormLabel>{t('fields.allowedPaymentMethods')}</FormLabel>
                  <div className="flex flex-col gap-2">
                    {availablePaymentMethods.map((type) => (
                      <FormItem
                        key={type}
                        className="flex flex-row items-center space-x-2 space-y-0"
                      >
                        <FormControl>
                          <Checkbox
                            checked={field.value.includes(type)}
                            onCheckedChange={(checked) => {
                              field.onChange(
                                checked === true
                                  ? [...field.value, type]
                                  : field.value.filter((v) => v !== type),
                              )
                            }}
                          />
                        </FormControl>
                        <FormLabel className="font-normal">
                          {te(`paymentMethods.types.${type}`)}
                        </FormLabel>
                      </FormItem>
                    ))}
                  </div>
                  {field.value.length === 0 && (
                    <FormDescription className="text-destructive">
                      {t('fields.allowedPaymentMethodsRequired')}
                    </FormDescription>
                  )}
                  <FormMessage />
                </FormItem>
              )}
            />

            <div>
              <Label className="mb-2 block">{t('fields.prices')}</Label>
              <EshopPricesFields control={form.control} />
            </div>

            <DialogFooter>
              <Button
                type="button"
                variant="outline"
                onClick={() => onOpenChange(false)}
              >
                {te('actions.cancel')}
              </Button>
              <Button type="submit">{te('actions.save')}</Button>
            </DialogFooter>
          </form>
        </Form>
      </DialogContent>
    </Dialog>
  )
}
